// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import checkoutSharedStyles from "@conklin/App/SharedStyles/CheckoutStyles";
import { PickUpPreferences, sharedDateTimeOptions, ShippingPreferences } from "@conklin/XDL/constants";
import { parseBoolean, Spacer } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Typography from "@insite/mobius/Typography";
import React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const OnePageCheckoutConfirmationShipping = ({ cart }: Props) => {
    if (!cart) {
        return null;
    }

    return (
        <>
            <Typography as="h3" {...checkoutSharedStyles.containerTitle}>
                {cart.fulfillmentMethod === FulfillmentMethod.PickUp ? translate("Pick Up") : translate("Shipping")}
            </Typography>
            <StyledWrapper {...checkoutSharedStyles.container}>
                {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <StyledWrapper {...checkoutSharedStyles.section}>
                        <Typography {...checkoutSharedStyles.sectionTitle}>{translate("Shipping Method")}</Typography>
                        <Typography
                            as="p"
                            {...checkoutSharedStyles.sectionValue}
                            data-test-selector="orderConfirmation_deliveryMethod"
                        >
                            {cart.carrier && cart.shipVia ? `${cart.shipVia.description}` : translate("Ship to home")}
                        </Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...checkoutSharedStyles.section}>
                    {cart.fulfillmentMethod === FulfillmentMethod.PickUp ? (
                        <>
                            <StyledWrapper>
                                <Typography {...checkoutSharedStyles.sectionTitle}>
                                    {translate("Pick Up Preference")}
                                </Typography>
                                <Typography {...checkoutSharedStyles.sectionValue}>
                                    {parseBoolean((cart.properties.pickUpWhenComplete || "false").toLowerCase())
                                        ? translate(PickUpPreferences.PickUpWhenComplete)
                                        : translate(PickUpPreferences.PickUpWhenReady)}
                                </Typography>
                            </StyledWrapper>
                            {cart.requestedPickupDate && (
                                <>
                                    <Spacer space={24} />
                                    <StyledWrapper>
                                        <Typography {...checkoutSharedStyles.sectionTitle}>
                                            {translate("Requested Pick Up Date")}
                                        </Typography>
                                        <Typography {...checkoutSharedStyles.sectionValue}>
                                            <LocalizedDateTime
                                                dateTime={cart.requestedPickupDateDisplay}
                                                options={sharedDateTimeOptions}
                                            />
                                        </Typography>
                                    </StyledWrapper>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <StyledWrapper>
                                <Typography {...checkoutSharedStyles.sectionTitle}>
                                    {translate("Shipping Preference")}
                                </Typography>
                                <Typography {...checkoutSharedStyles.sectionValue}>
                                    {parseBoolean((cart.properties.shipWhenComplete || "false").toLowerCase())
                                        ? translate(ShippingPreferences.ShipWhenComplete)
                                        : translate(ShippingPreferences.ShipWhenAvailable)}
                                </Typography>
                            </StyledWrapper>
                            {cart.requestedDeliveryDate && (
                                <>
                                    <Spacer space={24} />
                                    <StyledWrapper>
                                        <Typography {...checkoutSharedStyles.sectionTitle}>
                                            {translate("Requested Delivery Date")}
                                        </Typography>
                                        <Typography {...checkoutSharedStyles.sectionValue}>
                                            <LocalizedDateTime
                                                dateTime={cart.requestedDeliveryDateDisplay}
                                                options={sharedDateTimeOptions}
                                            />
                                        </Typography>
                                    </StyledWrapper>
                                </>
                            )}
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationShipping),
    definition: {
        displayName: "Shipping",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
